import type { LucideProps } from "lucide-react";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ComboBox,
  type ComboOption,
} from "@/components/shared/tables/tableComboBox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

type Props = {
  name: string;
  label?: string;
  options: ComboOption[];
  autoFocus?: boolean;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  sideOffset?: number;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  enableClear?: boolean;
  highlightWhenSelected?: boolean;
};

export const ComboBoxInput = ({
  label,
  name,
  options,
  fullWidth = true,
  placeholder = "",
  description,
  className,
  sideOffset,
  disabled,
  icon,
  enableClear,
  highlightWhenSelected,
}: Props) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const items =
    options?.map(
      (option): ComboOption => ({
        label: option.label ?? "",
        value: option.value,
        subLabel: option?.subLabel ?? undefined,
        alternativeLabels: [],
      }),
    ) ?? [];

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const currentOption = options.find(
          (option) => option.value === field.value,
        );

        const value = currentOption
          ? {
              label: currentOption.label ?? "",
              value: currentOption.value,
              subLabel: currentOption.subLabel ?? undefined,
              alternativeLabels: [],
            }
          : undefined;

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <ComboBox
                currentOption={value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                items={items}
                fullWidth={fullWidth}
                placeholder={placeholder}
                emptyLabel={t("empty_list")}
                variant={"outline"}
                className={className}
                sideOffset={sideOffset}
                disabled={disabled}
                icon={icon}
                enableClear={enableClear}
                highlightWhenSelected={highlightWhenSelected}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
